import { FormEvent, useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'

import { discussionCsApi } from './chapterDiscussions.api'
import {
  buildCommentQuery,
  discussionCommentIdentityKey,
} from './chapterDiscussions.model'

import type {
  DiscussionCommentListItem,
  ListDiscussionCommentsQuery,
  ModerationStatus,
  VisibilityStatus,
} from './chapterDiscussions.types'

export interface DiscussionModerationListProps {
  initialQuery?: ListDiscussionCommentsQuery
}

type FilterType =
  | 'all'
  | 'moderation'
  | 'visibility'
  | 'policy'
  | 'book'
  | 'author'
  | 'exact'

export function DiscussionModerationList({
  initialQuery = {},
}: DiscussionModerationListProps) {
  const navigate = useNavigate()
  const requestVersion = useRef(0)
  const [query, setQuery] = useState<ListDiscussionCommentsQuery>(() =>
    buildCommentQuery({ ...initialQuery, limit: initialQuery.limit ?? 50 }),
  )
  const [items, setItems] = useState<DiscussionCommentListItem[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filterType, setFilterType] = useState<FilterType>('all')
  const [status, setStatus] = useState<ModerationStatus>('needs_review')
  const [visibility, setVisibility] = useState<VisibilityStatus>('visible')
  const [value, setValue] = useState('')
  const [chapterId, setChapterId] = useState('')

  const load = useCallback(
    async (cursor?: string, append = false) => {
      const version = ++requestVersion.current
      setLoading(true)
      setError(null)
      try {
        const response = await discussionCsApi.listComments({
          ...query,
          cursor,
        })
        if (version !== requestVersion.current) return
        setItems(current =>
          append ? [...current, ...response.items] : response.items,
        )
        setNextCursor(response.nextCursor)
      } catch (requestError) {
        if (version === requestVersion.current) {
          setError(
            requestError instanceof Error
              ? requestError.message
              : 'Unable to load moderation comments',
          )
        }
      } finally {
        if (version === requestVersion.current) setLoading(false)
      }
    },
    [query],
  )

  useEffect(() => {
    load()
  }, [load])

  const applyFilter = (event: FormEvent) => {
    event.preventDefault()
    try {
      const base = { limit: 50 }
      const next =
        filterType === 'moderation'
          ? { ...base, moderationStatus: status }
          : filterType === 'visibility'
            ? { ...base, visibilityStatus: visibility }
            : filterType === 'policy'
              ? { ...base, policyReason: value.trim() }
              : filterType === 'book'
                ? {
                    ...base,
                    bookId: Number(value),
                    chapterId: chapterId ? Number(chapterId) : undefined,
                  }
                : filterType === 'author'
                  ? { ...base, authorId: Number(value) }
                  : filterType === 'exact'
                    ? { ...base, exactCommentId: value.trim() }
                    : base
      setQuery(buildCommentQuery(next))
    } catch (filterError) {
      setError(
        filterError instanceof Error ? filterError.message : 'Invalid filter',
      )
    }
  }

  const needsValue = !['all', 'moderation', 'visibility'].includes(filterType)

  return (
    <Box>
      <Paper component="form" onSubmit={applyFilter} sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          useFlexGap
          flexWrap="wrap"
          alignItems={{ xs: 'stretch', md: 'flex-end' }}>
          <TextField
            select
            label="Filter"
            value={filterType}
            sx={{ width: { xs: '100%', md: 210 } }}
            onChange={event => setFilterType(event.target.value as FilterType)}>
            <MenuItem value="all">All comments</MenuItem>
            <MenuItem value="moderation">AI/moderation state</MenuItem>
            <MenuItem value="visibility">Public state</MenuItem>
            <MenuItem value="policy">Policy reason</MenuItem>
            <MenuItem value="book">Book / chapter</MenuItem>
            <MenuItem value="author">Author ID</MenuItem>
            <MenuItem value="exact">Exact comment ID</MenuItem>
          </TextField>
          {filterType === 'moderation' && (
            <TextField
              select
              label="Moderation state"
              value={status}
              sx={{ width: { xs: '100%', md: 180 } }}
              onChange={event =>
                setStatus(event.target.value as ModerationStatus)
              }>
              {[
                'queued',
                'approved',
                'rejected',
                'needs_review',
                'error',
                'rate_limited',
                'invalid',
              ].map(option => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          )}
          {filterType === 'visibility' && (
            <TextField
              select
              label="Public state"
              value={visibility}
              sx={{ width: { xs: '100%', md: 160 } }}
              onChange={event =>
                setVisibility(event.target.value as VisibilityStatus)
              }>
              {['pending', 'visible', 'hidden', 'deleted'].map(option => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          )}
          {needsValue && (
            <TextField
              required
              label={
                filterType === 'book'
                  ? 'Book ID'
                  : filterType === 'author'
                    ? 'Author ID'
                    : filterType === 'policy'
                      ? 'Policy reason'
                      : 'Comment ID'
              }
              type={
                filterType === 'book' || filterType === 'author'
                  ? 'number'
                  : 'text'
              }
              value={value}
              sx={{ width: { xs: '100%', md: 220 } }}
              onChange={event => setValue(event.target.value)}
            />
          )}
          {filterType === 'book' && (
            <TextField
              label="Chapter ID (optional)"
              type="number"
              value={chapterId}
              sx={{ width: { xs: '100%', md: 220 } }}
              onChange={event => setChapterId(event.target.value)}
            />
          )}
          <Button
            type="submit"
            variant="contained"
            size="small"
            sx={{
              alignSelf: 'flex-end',
              ml: 'auto',
              mb: { xs: 0, md: 0.5 },
              minWidth: 112,
            }}>
            Apply filter
          </Button>
        </Stack>
      </Paper>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Comment</TableCell>
              <TableCell>Book / chapter</TableCell>
              <TableCell>Author</TableCell>
              <TableCell>Moderation</TableCell>
              <TableCell>Visibility</TableCell>
              <TableCell>Reports</TableCell>
              <TableCell>Content</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map(item => (
              <TableRow
                hover
                key={`${item.comment.bookId}:${item.comment.chapterId}:${item.comment.commentId}`}
                sx={{ cursor: 'pointer' }}
                onClick={() =>
                  navigate(
                    `/chapter-discussions/${discussionCommentIdentityKey(
                      item.comment,
                    )}/show`,
                  )
                }>
                <TableCell>{item.comment.commentId}</TableCell>
                <TableCell>
                  {item.comment.bookId} / {item.comment.chapterId}
                </TableCell>
                <TableCell>{item.comment.authorId}</TableCell>
                <TableCell>
                  <Chip size="small" label={item.comment.moderationStatus} />
                </TableCell>
                <TableCell>
                  <Chip size="small" label={item.comment.visibilityStatus} />
                </TableCell>
                <TableCell>{item.unresolvedReportCount}</TableCell>
                <TableCell sx={{ maxWidth: 360 }}>
                  <Typography variant="body2" noWrap>
                    {item.comment.body}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            {!loading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No comments match this filter.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack alignItems="center" sx={{ mt: 2 }}>
        {loading ? (
          <CircularProgress size={28} />
        ) : (
          nextCursor && (
            <Button onClick={() => load(nextCursor, true)}>Load more</Button>
          )
        )}
      </Stack>
    </Box>
  )
}
